import {
  AgentChatTransferStatus,
  ChatStatus,
  MessagingProvider,
} from '@src/model/frontendmodel';
import type { RootState } from '@src/store/reduxstore';
import { ChatTray } from '@src/types/chats';
import { Chat, ContactAccountOwner, Selector } from '@types';
import { selectHasCallInActiveChat } from './calls';
import { activeChatSelector } from './chats';

export const getCurrentTrayName: Selector<string> = (state: RootState) =>
  state.agentConsole.chatRequests.focused ? 'INCOMING' : 'ONGOING';

export const getTotalOngoingChats: Selector<number> = (state: RootState) =>
  state.agentConsole.chatsOngoing.total;

export const getTotalIncomingChats: Selector<number> = (state: RootState) =>
  state.agentConsole.chatRequests.total;

export const getOngoingTraySize: Selector<number> = (state: RootState) =>
  Object.keys(state.agentConsole.chatsOngoing.items).length;

export const getIncomingTraySize: Selector<number> = (state: RootState) =>
  Object.keys(state.agentConsole.chatRequests.items).length;

export const isLoadingIncomingTray: Selector<boolean> = (state: RootState) =>
  state.agentConsole.chatRequests.isLoadingChats;

export const isLoadingOngoingTray: Selector<boolean> = (state: RootState) =>
  state.agentConsole.chatsOngoing.isLoadingChats;

export default getCurrentTrayName;

export const agentChatSelect = (chatId: string) => (state: RootState) =>
  state.agentConsole.chats[chatId];

export const activeChatSelect: Selector<Chat | undefined> = state => {
  const chatId = state.agentConsole?.chatsOngoing?.activeChat?.chatId;
  return chatId && state.agentConsole.chats[chatId];
};

export const activeChatContact: Selector<
  ContactAccountOwner | undefined
> = state => {
  const activeChat = activeChatSelect(state);

  if (
    activeChat?.contactAccountId &&
    Object.prototype.hasOwnProperty.call(
      state.agentConsole.messagingAccounts,
      activeChat.contactAccountId
    )
  ) {
    const msgAccount =
      state.agentConsole.messagingAccounts[activeChat.contactAccountId];

    // Find the contact who established the chat
    if (
      msgAccount.contactId &&
      Object.prototype.hasOwnProperty.call(
        state.agentConsole.contacts,
        msgAccount.contactId
      )
    ) {
      return state.agentConsole.contacts[msgAccount.contactId];
    }
  }

  return undefined;
};

export const isChatComposerDisabled: Selector<boolean> = state => {
  const activeChat = activeChatSelector(state) as ChatTray;

  return isInputMessageDisabled(state, activeChat);
};

export const isComposerToolbarDisabled: Selector<boolean> = state => {
  const activeChat = activeChatSelector(state) as ChatTray;

  return (
    isChatComposerDisabled(state) ||
    isTransferInProgress(state) ||
    Object.keys(activeChat).length === 0
  );
};

export const isTransferInProgress: Selector<boolean> = state => {
  const activeChat = activeChatSelector(state) as ChatTray;
  const agentId = state.loginAuthentication?.success?.id;

  if (activeChat?.chatXferRequest) {
    const transfer = activeChat.chatXferRequest;
    return (
      transfer.sourceAgent.id !== agentId ||
      transfer.status === AgentChatTransferStatus.ACCEPTED
    );
  }

  return false;
};

export const isInputMessageDisabled = (state: RootState, chat: ChatTray) => {
  const { gotMsgFromContactInPast24h } =
    state.agentConsole.currentContactChatHistory;

  const activeChatStatus = chat?.status;
  const isServiceWindowExpired =
    !!state.chats?.serviceWindowExpiredChatIds?.[chat?.id];

  return (
    activeChatStatus === ChatStatus.CLOSED_BY_CONTACT ||
    activeChatStatus === ChatStatus.LEFT_BY_CONTACT ||
    activeChatStatus === ChatStatus.BOT_CHATTING ||
    isServiceWindowExpired ||
    (chat?.hasPassedMaxTime && !gotMsgFromContactInPast24h)
  );
};

export const isOngoingFocused = (state: RootState) =>
  state.agentConsole.chatsOngoing.focused;

export const selectConnectionLost = (state: RootState): boolean =>
  state.agentConsole.backendConn.closeEvent;

export const selectIsOutgoingCallDisabled: Selector<boolean> = state => {
  const activeChat = activeChatSelector(state);
  const activeChatHasCall = selectHasCallInActiveChat(state);
  const activeChatIsBSP =
    activeChat?.provider === MessagingProvider.WHATSAPPB2CHAT;

  return (
    !activeChat?.id ||
    !activeChatIsBSP ||
    activeChatHasCall ||
    isTransferInProgress(state) ||
    activeChat?.status === ChatStatus.BOT_CHATTING
  );
};
